import React, {useEffect, useState} from 'react'
import {DropdownInput, TextInput} from "atmosfair-ui";
import {convertAgenciesFromDropdownFormat, convertAgenciesToDropdownFormat} from "../../../utils/converter/dropdown";
import {AgencyProps} from "../../../api/model/agency";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import {validateAgency, validTravelComponentsOrJounrneyName} from "../utils";
import {toString} from "../../../utils/string/common";

interface NameDescriptionAgencyFormProps {
    agencies: AgencyProps[],
    defaultValues: NameDescriptionAgencyFormFormDataProps | null,
    nameLabel: string,
    validateForm: number,
    onFormIsValid: (data: ValidNameDescriptionAgencyFormDataProps) => void,
    setAgencySelection?: (agency: AgencyProps | null) => void,
    hideAgencySelection: boolean,
}

export interface ValidNameDescriptionAgencyFormDataProps extends NameDescriptionAgencyFormFormDataProps {
    agency: AgencyProps
}

export interface NameDescriptionAgencyFormFormDataProps {
    name: string | number,
    description: string,
    agency: AgencyProps | null
}

const setInitialFormData = (initialValues: NameDescriptionAgencyFormFormDataProps | null) => {
    if (initialValues === null) return {
        name: '',
        description: '',
        agency: null,
    }
    return {
        name: initialValues.name,
        description: initialValues?.description ?? "",
        agency: initialValues.agency
    }
}

const NameDescriptionAgencyForm = ({
                                       agencies,
                                       defaultValues,
                                       nameLabel,
                                       validateForm,
                                       onFormIsValid,
                                       setAgencySelection,
                                       hideAgencySelection,
                                   }: NameDescriptionAgencyFormProps) => {
    const [formData, setFormData] = useState<NameDescriptionAgencyFormFormDataProps>(setInitialFormData(defaultValues))
    const [agency, setAgency] = useState(agencies.length === 1 ? agencies[0] : null)
    const [formErrorData, setFormErrorData] = useState({
        nameError: false,
        agencyError: false,
        descriptionError: false,
    })
    const {
        nameError,
        agencyError,
        descriptionError,
    } = formErrorData
    const {
        name,
        description,
    } = formData
    const moreThanOneAgency = agencies.length > 1

    useEffect(() => {
        if (hideAgencySelection && defaultValues?.agency === null) throw new Error("You cannot set hideAgencySelection to true and do not pass a defaultAgency!")
    }, []);

    useEffect(() => {
        if (defaultValues?.agency !== null) setAgency(defaultValues?.agency ?? null)
    }, [defaultValues?.agency]);

    useEffect(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER && _validateForm()) {
            const {name, description} = formData
            onFormIsValid({
                name: toString(name).trim(),
                description: description?.trim(),
                agency: agency!!
            })
        }
    }, [validateForm])

    useEffect(() => {
        if (agencies.length === 1) setAgency(agencies[0])
    }, [agencies])

    const _validateForm = () => {
        const validTravelComponentName = validTravelComponentsOrJounrneyName(toString(name))
        let validAgency = true
        if (moreThanOneAgency) validAgency = validateAgency(agency)
        let validDescription = true
        if (description && description.length > 0) validDescription = description.length > 3 && description.length < 1000
        setFormErrorData({
            nameError: !validTravelComponentName,
            agencyError: !validAgency,
            descriptionError: !validDescription,
        })
        return [
            validTravelComponentName,
            validDescription,
            validAgency
        ].every(Boolean)
    }

    const _updateFormData = <K extends keyof NameDescriptionAgencyFormFormDataProps>(key: K, payload: NameDescriptionAgencyFormFormDataProps[K]) => setFormData({
        ...formData,
        [key]: payload
    })

    const _setAgencyDropdown = () => {
        if (hideAgencySelection) return null
        if (moreThanOneAgency) return <DropdownInput
            aria-errormessage={"ERRORS.COMMON.NAME"}
            formError={agencyError}
            required={true}
            containerStyle={{width: '30%'}}
            label={"GENERAL.AGENCY"}
            defaultValue={defaultValues?.agency?.id}
            options={convertAgenciesToDropdownFormat(agencies)}
            setSelection={selection => {
                const convertedSelection = convertAgenciesFromDropdownFormat(selection)
                setAgencySelection && setAgencySelection(convertedSelection)
                setAgency(convertedSelection)
            }}
        />
        return null
    }

    return <>
        <div className={"form-row"}>
            <TextInput
                required={true}
                value={toString(name)}
                error={nameError}
                aria-errormessage={"ERRORS.COMMON.NAME"}
                containerStyle={{flex: 1}}
                onBlur={event => _updateFormData("name", event.target.value.trim())}
                onChange={event => _updateFormData("name", event.target.value)}
                placeholder={"GENERAL.NAME"}
                label={nameLabel}/>
            {_setAgencyDropdown()}
        </div>
        <div className={'form-row'}>
            <TextInput
                value={description}
                error={descriptionError}
                aria-errormessage={"ERRORS.COMMON.DESCRIPTION"}
                inputStyle={{resize: "vertical"}}
                containerClasses={"save-travel-components-dialog-description"}
                onBlur={event => _updateFormData("description", event.target.value.trim())}
                onChange={event => _updateFormData("description", event.target.value)}
                placeholder={"GENERAL.DESCRIPTION"}
                label={'GENERAL.DESCRIPTION'}
                multiline={true}/>
        </div>
    </>
}

export default NameDescriptionAgencyForm